import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import {Provider, SystemCode, TranslationRule} from '@helpers/types/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlarisEditPanelService, EditPanelInputData } from '@campaign-portal/components-library';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslationRulesService } from '../translation-rules.service';
import { SystemCodesService } from "../../system-codes/system-codes.service";
import {ProvidersService} from "@helpers/repo/providers.service";
import {AdditionalValidators} from "@helpers/shared/validators";

@Component({
	selector: 'app-edit-translation-rules',
	templateUrl: './edit-rule.component.html',
	styleUrl: './edit-rule.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditTranslationRulesComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{key: 'mask', value: 'gl.errors.invalid_format'},
		{key: 'required', value: 'gl.errors.required'}];
	readonly rule: TranslationRule = {
		ID: null,
		Name: null,
		Status: false,
		SourceType: null,
		ResponseCodes: null,
		SystemCode: null,
	};
	readonly form = new FormGroup({
		ID: new FormControl<number | null>(null),
		Name: new FormControl<string | null>(null, [Validators.required]),
		Status: new FormControl<boolean>(false),
		SourceType: new FormControl<string | null>(null, [Validators.required]),
		ResponseCodes: new FormControl<string | null>(null,
			[Validators.required, AdditionalValidators.uniqueValuesArrayValidator()]),
		SystemCode: new FormControl<string | null>(null, [Validators.required])
	});
	readonly ProvidersFilter = new FormControl();
	providers: Provider[] = [];

	readonly SystemCodeFilter = new FormControl();
	codes: SystemCode[] = [];

	readonly separator = ', ';
	private readonly destroyRef = inject(DestroyRef);
	
	constructor(
			@Inject(EditPanelInputData) private readonly inputData: { rule?: TranslationRule },
			public readonly service: TranslationRulesService,
			private readonly editPanel: AlarisEditPanelService,
			public providersService: ProvidersService,
			public readonly codesService: SystemCodesService
	) {
		if ( this.inputData.rule ) {
			this.rule = this.inputData.rule;
		}
	}
	
	ngOnInit(): void {
		this.reset();
		this.providersService.loadProviders('').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
		this.providersService.list$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((list) => {
			this.ProvidersFilter.reset();
			this.providers = list;
		});
		this.codesService.list$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((codes) => {
			if (codes) {
				this.codes = codes;
			}
			this.SystemCodeFilter.reset();
		});
		this.ProvidersFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.providers = this.providersService.list.filter((provider) =>
					provider?.Name.toLowerCase()
						.includes(value?.toLowerCase() ?? '')
				);
			});
		this.SystemCodeFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.codes = this.codesService.list.filter((code) =>
					code?.Value?.toLowerCase()
						.includes(value?.toLowerCase() ?? '') || code?.Description?.toLowerCase()
						.includes(value?.toLowerCase() ?? '')
				);
			});
	}
	
	reset(): void {
		const rule: TranslationRule = structuredClone(this.rule);
		this.form.reset(rule);
	}
	
	save(): void {
		const rule = {
			id: this.form.controls.ID.value,
			name: this.form.controls.Name.value,
			status: this.form.controls.Status.value,
			sourceType: this.form.controls.SourceType.value,
			responseCodes: this.form.controls.ResponseCodes.value,
			systemCodeID: this.getSystemCodeIDByValue(this.form.controls.SystemCode.value)
		};
		this.service.update(rule)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if (resp?.Success || resp?.CreatedID) {
					this.editPanel.close(true);
				}
			});
	}
	
	close(): void {
		this.editPanel.close(false);
	}

	getSystemCodeIDByValue(val: string | null): number | null {
		return this.codesService.list.filter((code) =>
			code?.Value === val)[0].ID
	}
}
